<template>
  <div class="container-fluid py-3">
    <b-card class="mb-3">
      <b-form-file
        v-model="file"
        accept=".xlsx,.xls"
        placeholder="Selecciona un archivo Excel..."
        browse-text="Examinar"
        @input="onFileSelected"
      />
      <div class="mt-2 d-flex align-items-center">
        <b-button
          variant="primary"
          :disabled="!file || uploading"
          @click="upload"
        >
          <b-spinner small v-if="uploading" class="mr-2"></b-spinner>
          Subir archivo
        </b-button>

        <b-badge v-if="currentBatchId" class="ml-3" variant="primary">
          Batch #{{ currentBatchId }} · OK: <strong>{{ badgeOk }}</strong> ·
          Errores: <strong>{{ badgeError }}</strong> · Total:
          <strong>{{ badgeTotal }}</strong>
        </b-badge>

        <b-progress
          v-if="uploading || uploadProgress > 0"
          class="ml-3 flex-grow-1"
          :max="100"
          height="20px"
          style="min-width: 240px"
        >
          <b-progress-bar
            :value="uploadProgress"
            :label="`${uploadProgress}%`"
            :variant="uploadProgress === 100 ? 'success' : 'info'"
            :animated="uploading"
            :striped="uploading"
          />
        </b-progress>
      </div>
    </b-card>

    <b-card v-if="batchOptions.length" class="mb-3">
      <div class="d-flex flex-wrap align-items-center">
        <b-form-group label="Batch:" class="mb-0 mr-3">
          <b-form-select
            v-model="currentBatchId"
            :options="batchOptions"
            @change="onBatchChange"
          />
        </b-form-group>

        <b-form-group label="Estado:" class="mb-0 mr-3" v-if="currentBatchId">
          <b-form-select
            id="statusSel"
            v-model="status"
            :options="statusOptions"
            @change="reloadRows(0)"
          />
        </b-form-group>

        <b-button
          v-if="currentBatchId"
          size="sm"
          class="mr-2 mt-2"
          variant="outline-primary"
          :disabled="totalRows === 0"
          @click="exportSelected"
        >
          Exportar Excel
        </b-button>

        <b-button
          v-if="currentBatchId"
          size="sm"
          class="mt-2"
          variant="outline-secondary"
          @click="deleteBatch(currentBatchId)"
        >
          Eliminar importación
        </b-button>
      </div>
    </b-card>

    <b-card v-if="currentBatchId">
      <div class="table-wrap">
      <b-table :items="rows" :fields="fields" small bordered class="minwide-table">
        <template #cell(parseStatus)="d">
          <b-badge
            :variant="d.item.parseStatus === 'OK' ? 'success' : 'danger'"
          >
            {{ d.item.parseStatus }}
          </b-badge>
        </template>
        <template #cell(fechaEmision)="d">{{
          fmtDate(d.item.fechaEmision)
        }}</template>
        <template #cell(fechaVencimiento)="d">{{
          fmtDate(d.item.fechaVencimiento)
        }}</template>
        <template #cell(errorComment)="d">
          <span v-if="d.item.errorComment" class="text-danger">{{
            d.item.errorComment
          }}</span>
        </template>
      </b-table>
      </div>

      <div class="d-flex justify-content-between align-items-center">
        <small class="text-muted">
          Mostrando {{ displayFrom }} - {{ displayTo }} de {{ displayTotal }}
        </small>
        <div>
          <b-button
            size="sm"
            class="mr-2"
            :disabled="pageStart === 0"
            @click="prevPage"
            >Anterior</b-button
          >
          <b-button
            size="sm"
            :disabled="pageStart + pageSize >= totalRows"
            @click="nextPage"
            >Siguiente</b-button
          >
        </div>
      </div>
    </b-card>

    <b-modal
      id="import-result-modal"
      v-model="modal.show"
      :title="modal.title"
      hide-footer
    >
      <p>{{ modal.text }}</p>
    </b-modal>
  </div>
</template>

<script>
import Vue from "vue";
import { BootstrapVue, BootstrapVueIcons, VBTooltip } from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import ImportBatchService from "@/services/ImportBatchService";
import * as XLSX from "xlsx";
Vue.use(BootstrapVue);
Vue.use(BootstrapVueIcons);
export default {
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  data() {
    return {
      file: null,
      uploading: false,
      uploadProgress: 0,
      currentBatchId: null,
      lastFilename: "",
      summary: { ok: 0, error: 0, total: 0 },

      status: "OK",
      statusOptions: [
        { text: "Solo OK", value: "OK" },
        { text: "Solo ERROR", value: "ERROR" },
        { text: "Todos", value: "ALL" },
      ],
      batchOptions: [],
      rows: [],
      totalRows: 0,
      pageStart: 0,
      pageSize: 20,

      fields: [
        {
          key: "parseStatus",
          label: "Estado",
          class: "text-center",
          tdClass: "align-middle",
        },
        { key: "fechaEmision", label: "F. Emisión"},
        { key: "fechaVencimiento", label: "F. Venc." },
        { key: "tipo", label: "Tipo" },
        { key: "serie", label: "Serie" },
        { key: "numero", label: "Número" },

        //{ key: 'docEntidad',       label: 'Doc Entidad' },
        { key: "ruc", label: "RUC" },
        { key: "nroDocumento", label: "Nro. Doc." },
        { key: "denominacion", label: "Denominación" },
        //{ key: 'ubigeo',           label: 'Ubigeo' },

        { key: 'monedaOriginal',   label: 'Moneda Original' },
        { key: 'tipoCambio',       label: 'T/C' },

        { key: "codProductoOriginal", label: "COD PRODUCTO (orig.)" },
        { key: "marcaAutoparteNombre", label: "Marca" },
        { key: "codigoOriginal", label: "Código Original" },
        { key: "codigoAlternativo", label: "Código Alterno" },

        { key: 'unidadMedida',     label: 'Unidad Medida' },
        { key: "cantidad", label: "Cant." },
        { key: 'valorUnit',        label: 'Valor Unit.' },
        { key: "precioUnit", label: "Precio Unit." },
        { key: 'totalGravada',     label: 'Total Gravada' },
        { key: 'igv',              label: 'IGV' },
        { key: "total", label: "Total" },

        { key: 'estado',           label: 'Estado Doc' },
        { key: 'guiasRemision',    label: 'Guías de Remisión' },

        { key: "errorComment", label: "Comentario" },
      ],

      modal: { show: false, title: "", text: "" },
    };
  },
  computed: {
    // badge numbers sin usar ??
    badgeOk() {
      const v = Number(this.summary && this.summary.ok);
      return Number.isFinite(v) ? v : 0;
    },
    badgeError() {
      const v = Number(this.summary && this.summary.error);
      return Number.isFinite(v) ? v : 0;
    },
    badgeTotal() {
      const tot = Number(this.summary && this.summary.total);
      if (Number.isFinite(tot)) return tot;
      return this.badgeOk + this.badgeError;
    },

    // tus contadores de tabla
    displayTotal() {
      return Number.isFinite(this.totalRows) ? this.totalRows : 0;
    },
    displayFrom() {
      return this.totalRows ? this.pageStart + 1 : 0;
    },
    displayTo() {
      return this.totalRows
        ? Math.min(this.pageStart + this.pageSize, this.totalRows)
        : 0;
    },
  },
  async mounted() {
    await this.loadBatchOptions();
    const saved = localStorage.getItem("gqr:lastBatchId");
    if (saved && this.batchOptions.some((o) => o.value === +saved)) {
      this.currentBatchId = +saved;
    } else if (this.batchOptions.length) {
      this.currentBatchId = this.batchOptions[0].value;
      localStorage.setItem("gqr:lastBatchId", String(this.currentBatchId));
    }
    if (this.currentBatchId) await this.refreshAfterUpload();
  },
  async activated() {
    const saved = localStorage.getItem("gqr:lastBatchId");

    if (saved && this.batchOptions.some((o) => o.value === +saved)) {
      if (+saved !== +this.currentBatchId) {
        this.currentBatchId = +saved;
      }
    } else if (this.batchOptions.length) {
      this.currentBatchId = this.batchOptions[0].value;
      localStorage.setItem("gqr:lastBatchId", String(this.currentBatchId));
    }

    if (this.currentBatchId) {
      await this.loadSummary();
      await this.reloadRows(this.pageStart || 0);
    }
  },
  methods: {
    onFileSelected(file) {
      this.file = file;
      this.uploadProgress = 0;
    },

    async loadBatchOptions() {
      const resp = await ImportBatchService.listBatches(
        { offset: 0, limit: 50 },
        this.$store
      );
      const data = resp?.data || resp || {};
      const items = Array.isArray(data.items) ? data.items : [];
      this.batchOptions = items.map((b) => ({
        value: b.id,
        text: `#${b.id} · ${b.filename} (OK:${b.ok} ERR:${b.error} TOT:${b.total})`,
      }));
    },
    async onBatchChange() {
      if (!this.currentBatchId) return;
      localStorage.setItem("gqr:lastBatchId", String(this.currentBatchId));
      await this.refreshAfterUpload();
    },

    async upload() {
      if (!this.file) return;
      try {
        this.uploading = true;
        this.uploadProgress = 0;
        const resp = await ImportBatchService.uploadSales(
          this.file,
          this.$store,
          (e) => {
            const total = e?.total || this.file?.size || 1;
            const loaded = e?.loaded || 0;
            const pct = Math.floor((loaded * 100) / total);
            this.uploadProgress = Math.min(99, Math.max(0, pct));
          }
        );
        const data = resp?.data || resp;
        this.uploadProgress = 100;

        this.currentBatchId = data?.batchId || null;
        this.lastFilename = data?.filename || this.file?.name || "";

        localStorage.setItem("gqr:lastBatchId", String(this.currentBatchId));

        await this.loadBatchOptions();

        await this.refreshAfterUpload();
        this.modal = {
          show: true,
          title: "Importación completada",
          text: data?.message || "Proceso finalizado.",
        };
        this.$bvModal.show("import-result-modal");
      } catch (err) {
        this.modal = {
          show: true,
          title: "Error",
          text: err?.message || "Ocurrió un error al subir el archivo",
        };
        this.$bvModal.show("import-result-modal");
      } finally {
        this.uploading = false;
        this.file = null;
        setTimeout(() => { this.uploadProgress = 0; }, 300);
      }
    },
    async exportSelected() {
      if (!this.currentBatchId) return;

      try {
        const rows = await this._fetchAllRowsForExport();

        if (!rows.length) {
          this.$bvToast.toast(
            "No hay filas para exportar con el filtro actual.",
            {
              title: "Sin datos",
              variant: "warning",
              solid: true,
            }
          );
          return;
        }

        const toDate = (v) => {
          if (!v) return "";
          const d = new Date(v);
          if (isNaN(d)) return "";
          const dd = String(d.getDate()).padStart(2, "0");
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const yy = d.getFullYear();
          return `${dd}/${mm}/${yy}`;
        };

        const data = rows.map((r) => ({
          ESTADO: r.parseStatus,
          "F. EMISIÓN": toDate(r.fechaEmision),
          "F. VENC.": toDate(r.fechaVencimiento),
          TIPO: r.tipo || "",
          SERIE: r.serie || "",
          NÚMERO: r.numero || "",
          "DOC ENTIDAD": r.docEntidad || "",
          RUC: r.ruc || "",
          "NRO. DOC.": r.nroDocumento || "",
          DENOMINACIÓN: r.denominacion || "",
          UBIGEO: r.ubigeo || "",
          "MONEDA ORIGINAL": r.monedaOriginal || "",
          "T/C": r.tipoCambio ?? "",
          "COD PRODUCTO (ORIG.)": r.codProductoOriginal || "",
          MARCA: r.marcaAutoparteNombre || "",
          "CÓDIGO ORIGINAL": r.codigoOriginal || "",
          "CÓDIGO ALTERNO": r.codigoAlternativo || "",
          "UNIDAD MEDIDA": r.unidadMedida || "",
          "CANT.": r.cantidad ?? "",
          "VALOR UNIT.": r.valorUnit ?? "",
          "PRECIO UNIT.": r.precioUnit ?? "",
          "TOTAL GRAVADA": r.totalGravada ?? "",
          IGV: r.igv ?? "",
          TOTAL: r.total ?? "",
          "ESTADO DOC": r.estado || "",
          "GUIAS DE REMISIÓN": r.guiasRemision || "",
          COMENTARIO: r.errorComment || "",
        }));

        const ws = XLSX.utils.json_to_sheet(data, { cellDates: false });
        ws["!cols"] = autoCols(data);

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(
          wb,
          ws,
          this.status === "OK"
            ? "OK"
            : this.status === "ERROR"
            ? "ERRORES"
            : "TODOS"
        );

        const safeStatus = this.status.toLowerCase();
        const filename = `batch_${this.currentBatchId}_${safeStatus}.xlsx`;
        XLSX.writeFile(wb, filename, { bookType: "xlsx", compression: true });
      } catch (e) {
        console.error(e);
        this.$bvToast.toast("No se pudo exportar el Excel", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }

      function autoCols(rows) {
        if (!rows.length) return [];
        const headers = Object.keys(rows[0]);
        const widths = headers.map((h) => Math.max(10, String(h).length));
        for (const row of rows) {
          headers.forEach((h, i) => {
            const len = String(row[h] ?? "").length;
            widths[i] = Math.min(60, Math.max(widths[i], len + 2));
          });
        }
        return widths.map((w) => ({ wch: w }));
      }
    },

    async _fetchAllRowsForExport() {
      const all = [];
      let offset = 0;
      const limit = 500;

      const firstResp = await ImportBatchService.listRows(
        this.currentBatchId,
        { status: this.status, offset, limit },
        this.$store
      );
      let payload = firstResp?.data || firstResp || {};
      let total = Number(payload.total) || 0;
      all.push(...(Array.isArray(payload.rows) ? payload.rows : []));

      offset += limit;
      while (offset < total) {
        const resp = await ImportBatchService.listRows(
          this.currentBatchId,
          { status: this.status, offset, limit },
          this.$store
        );
        payload = resp?.data || resp || {};
        all.push(...(Array.isArray(payload.rows) ? payload.rows : []));
        offset += limit;
      }

      return all;
    },

    async refreshAfterUpload() {
      if (!this.currentBatchId) return;
      await this.loadSummary();
      await this.reloadRows(0);
    },

    async loadSummary() {
      try {
        const resp = await ImportBatchService.getSummary(
          this.currentBatchId,
          this.$store
        );
        const s = resp?.data || resp || {};
        this.summary = {
          ok: Number(s.ok) || 0,
          error: Number(s.error) || 0,
          total:
            Number(s.total) || (Number(s.ok) || 0) + (Number(s.error) || 0),
        };
      } catch (e) {
        this.summary = { ok: 0, error: 0, total: 0 };
      }
    },

    async reloadRows(start = this.pageStart) {
      this.pageStart = start;
      try {
        const resp = await ImportBatchService.listRows(
          this.currentBatchId,
          { status: this.status, offset: this.pageStart, limit: this.pageSize },
          this.$store
        );
        const data = resp?.data || resp || {};
        this.rows = Array.isArray(data.rows) ? data.rows : [];
        this.totalRows = Number(data.total) || 0;
      } catch (e) {
        this.rows = [];
        this.totalRows = 0;
      }
    },

    onStatusChange() {
      this.reloadRows(0); // reset a primera página al cambiar filtro
    },

    prevPage() {
      if (this.pageStart > 0) this.reloadRows(this.pageStart - this.pageSize);
    },
    nextPage() {
      if (this.pageStart + this.pageSize < this.totalRows)
        this.reloadRows(this.pageStart + this.pageSize);
    },

    async downloadErrors() {
      if (!this.currentBatchId) return;
      try {
        await ImportBatchService.exportErrors(this.currentBatchId, this.$store);
      } catch (e) {
        this.$bvToast.toast("No se pudo descargar el archivo de errores", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }
    },

    async deleteBatch(batchId) {
      try {
        await ImportBatchService.deleteBatch(batchId, this.$store);
        await this.loadBatchOptions();

        if (String(this.currentBatchId) === String(batchId)) {
          localStorage.removeItem("gqr:lastBatchId");

          if (this.batchOptions.length) {
            this.currentBatchId = this.batchOptions[0].value;
            localStorage.setItem(
              "gqr:lastBatchId",
              String(this.currentBatchId)
            );
            await this.refreshAfterUpload();
          } else {
            this.currentBatchId = null;
            this.rows = [];
            this.totalRows = 0;
            this.summary = { ok: 0, error: 0, total: 0 };
          }
        }

        this.$bvToast.toast("Importación eliminada", {
          title: "Hecho",
          variant: "success",
          solid: true,
        });
      } catch {
        this.$bvToast.toast("No se pudo eliminar la importación", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }
    },

    fmtDate(v) {
      if (!v) return "";
      const d = new Date(v);
      return isNaN(d.getTime()) ? "" : d.toLocaleDateString();
    },
  },
};
</script>

<style scoped>
.table-wrap { overflow-x: auto; }
.minwide-table { min-width: 1600px; }
</style>
